/**
 * Data Hub — KPI Category Scores tab.
 *
 * Composite and per-category tiles up top, then underline tabs for Per-KPI
 * scores vs Trend (composite) so the Insights view stays one composition.
 */
import { useMemo, useState } from "react";

import { AlertTriangle } from "lucide-react";

import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { TabCard, TabError, TabLoading } from "../components/TabState";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

type TrendPoint = { period_start: string; composite: number | null };
type TrendRange = "3" | "6" | "12";
type DetailTab = "per-kpi" | "trend";

const DETAIL_TABS = [
  { id: "per-kpi", label: "Per-KPI scores" },
  { id: "trend", label: "Trend (composite)" },
];

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function scoreTone(v: number | null) {
  if (v == null) return "border border-gray-200 bg-gray-50 text-gray-400";
  if (v >= 80) return "border border-green-200 bg-green-50 text-green-900";
  if (v >= 50) return "border border-amber-200 bg-amber-50 text-amber-900";
  return "border border-red-200 bg-red-50 text-red-900";
}

function ScoreTile({
  label,
  score,
  featured,
}: {
  label: string;
  score: number | null;
  featured?: boolean;
}) {
  const shell = featured
    ? "border-2 border-[#974ae4] bg-[#f5ebff]"
    : scoreTone(score);
  const labelTone = featured ? "text-[#7a36c4] font-semibold" : "opacity-80";
  const scoreToneClass = featured ? "text-gray-900" : "";

  return (
    <div
      className={`flex min-h-[72px] flex-col justify-between gap-1.5 rounded-xl px-3 py-2.5 ${shell}`}
    >
      <p
        className={`m-0 line-clamp-2 text-[10px] uppercase leading-snug tracking-wide ${labelTone}`}
      >
        {label}
      </p>
      <p
        className={`m-0 text-xl font-semibold tabular-nums leading-none ${scoreToneClass}`}
      >
        {score != null ? score.toFixed(1) : "—"}
      </p>
    </div>
  );
}

function parseTrendDate(periodStart: string): Date | null {
  if (!periodStart) return null;
  const iso = Date.parse(periodStart);
  if (!Number.isNaN(iso)) return new Date(iso);

  const named = periodStart.match(/^([A-Za-z]{3}),?\s*(\d{4})$/);
  if (named) {
    const month = MONTH_SHORT.findIndex(
      m => m.toLowerCase() === named[1].toLowerCase()
    );
    if (month >= 0) return new Date(Number(named[2]), month, 1);
  }

  const ymd = periodStart.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3] ?? 1));
  }

  return null;
}

function formatMonthLabel(periodStart: string): string {
  const d = parseTrendDate(periodStart);
  if (!d) return periodStart || "—";
  return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function sortTrendPoints(points: TrendPoint[]): TrendPoint[] {
  return [...points].sort((a, b) => {
    const da = parseTrendDate(a.period_start)?.getTime() ?? 0;
    const db = parseTrendDate(b.period_start)?.getTime() ?? 0;
    return da - db;
  });
}

function filterTrendByRange(
  points: TrendPoint[],
  range: TrendRange
): TrendPoint[] {
  const sorted = sortTrendPoints(points).filter(p => p.period_start);
  const n = Number(range);
  return sorted.slice(Math.max(0, sorted.length - n));
}

function TrendChart({ points }: { points: TrendPoint[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (points.length === 0) {
    return (
      <p className="m-0 py-8 text-center text-sm text-gray-500">
        No composite trend points yet.
      </p>
    );
  }

  const w = 720;
  const h = 220;
  const padL = 36;
  const padR = 20;
  const padT = 24;
  const padB = 40;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  const xs = (i: number) =>
    padL +
    (points.length === 1 ? plotW / 2 : (i * plotW) / (points.length - 1));
  const ys = (v: number) => padT + plotH - (v / 100) * plotH;

  const linePts = points
    .map((p, i) => (p.composite == null ? null : `${xs(i)},${ys(p.composite)}`))
    .filter(Boolean) as string[];
  const d =
    linePts.length === 0
      ? ""
      : linePts.map((pt, i) => `${i === 0 ? "M" : "L"}${pt}`).join(" ");

  const hover = hoverIdx != null ? points[hoverIdx] : null;
  const hoverX = hoverIdx != null ? xs(hoverIdx) : 0;
  const hoverY =
    hover?.composite != null ? ys(hover.composite) : padT + plotH / 2;

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full"
        role="img"
        aria-label="Composite score trend by month"
        onMouseLeave={() => setHoverIdx(null)}
      >
        {[0, 25, 50, 75, 100].map(g => (
          <g key={g}>
            <line
              x1={padL}
              x2={w - padR}
              y1={ys(g)}
              y2={ys(g)}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
            <text
              x={padL - 8}
              y={ys(g) + 3}
              fontSize={10}
              fill="#9ca3af"
              textAnchor="end"
            >
              {g}
            </text>
          </g>
        ))}

        {d ? (
          <path d={d} fill="none" stroke="#974ae4" strokeWidth={2.25} />
        ) : null}

        {hoverIdx != null && hover?.composite != null && (
          <>
            <line
              x1={hoverX}
              x2={hoverX}
              y1={padT}
              y2={h - padB}
              stroke="#c4b5fd"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            <line
              x1={padL}
              x2={w - padR}
              y1={hoverY}
              y2={hoverY}
              stroke="#c4b5fd"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
          </>
        )}

        {points.map((p, i) => {
          const active = hoverIdx === i;
          const y = p.composite != null ? ys(p.composite) : null;
          return (
            <g key={`${p.period_start}-${i}`}>
              {y != null && (
                <circle
                  cx={xs(i)}
                  cy={y}
                  r={active ? 5.5 : 3.5}
                  fill="#974ae4"
                  stroke={active ? "#fff" : "none"}
                  strokeWidth={active ? 2 : 0}
                  className="transition-[r] duration-150"
                />
              )}
              {/* Larger hit target for hover */}
              <circle
                cx={xs(i)}
                cy={y ?? padT + plotH / 2}
                r={14}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoverIdx(i)}
              />
              <text
                x={xs(i)}
                y={h - 12}
                fontSize={10}
                fill={active ? "#7a36c4" : "#6b7280"}
                fontWeight={active ? 600 : 400}
                textAnchor="middle"
              >
                {points.length > 8 && i % 2 === 1 && !active
                  ? ""
                  : formatMonthLabel(p.period_start)}
              </text>
            </g>
          );
        })}
      </svg>

      {hover && hoverIdx != null && (
        <div
          className="pointer-events-none absolute z-10 min-w-[120px] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md"
          style={{
            left: `${(hoverX / w) * 100}%`,
            top: Math.max(8, (hoverY / h) * 100 - 18) + "%",
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="m-0 text-[11px] font-medium text-gray-500">
            {formatMonthLabel(hover.period_start)}
          </p>
          <p className="m-0 mt-0.5 text-sm font-semibold tabular-nums text-gray-900">
            Composite{" "}
            {hover.composite != null ? hover.composite.toFixed(1) : "—"}
          </p>
          <p className="m-0 text-[10px] text-gray-400">Score scale 0–100</p>
        </div>
      )}
    </div>
  );
}

export function CategoryScoresTab() {
  const [dataset] = useActiveDataset();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getCategoryScores(),
    [dataset]
  );
  const [category, setCategory] = useState("all");
  const [term, setTerm] = useState("");
  const [trendRange, setTrendRange] = useState<TrendRange>("6");
  const [detailTab, setDetailTab] = useState<DetailTab>("per-kpi");

  const perKpi = useMemo(() => {
    let out = data?.per_kpi ?? [];
    if (category !== "all") out = out.filter(k => k.category_key === category);
    if (term.trim()) {
      const t = term.trim().toLowerCase();
      out = out.filter(k => k.metric_name.toLowerCase().includes(t));
    }
    return out;
  }, [data, category, term]);

  const trendPoints = useMemo(
    () => filterTrendByRange(data?.trend ?? [], trendRange),
    [data?.trend, trendRange]
  );

  const kpiColumns = useMemo(
    () => [
      {
        key: "metric_name",
        title: "Metric",
        render: (_: unknown, k: { metric_name: string; metric_id: string }) => (
          <>
            <span className="text-gray-900">{k.metric_name}</span>
            <span className="ml-2 font-mono text-[11px] text-gray-400">
              {k.metric_id}
            </span>
          </>
        ),
      },
      {
        key: "category_key",
        title: "Category",
        render: (value: string) => (
          <span className="capitalize text-gray-600">{value}</span>
        ),
      },
      {
        key: "effective_share",
        title: "Effective share",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums text-gray-600">
            {(value ?? 0).toFixed(1)}%
          </span>
        ),
      },
      {
        key: "n",
        title: "n",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums text-gray-600">{value ?? 0}</span>
        ),
      },
      {
        key: "score",
        title: "Score",
        textalign: "left" as const,
        render: (value: number | null) => (
          <span
            className={`inline-block min-w-[52px] rounded px-2 py-0.5 text-left text-xs font-semibold ${scoreTone(value)}`}
          >
            {value != null ? value.toFixed(1) : "—"}
          </span>
        ),
      },
    ],
    []
  );

  if (loading) return <TabLoading label="Loading KPI category scores…" />;
  if (error) return <TabError error={error} />;
  if (!data) return null;

  return (
    <div>
      {data.label_drift && (
        <div className="mx-4 mt-4 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div className="text-xs text-amber-900">
            <p className="m-0 font-semibold">Category label drift detected</p>
            <p className="m-0">
              Code expects: {data.label_drift.expected.join(", ")}
            </p>
            <p className="m-0">
              Definitions use: {data.label_drift.found.join(", ")}
            </p>
          </div>
        </div>
      )}

      <TabCard
        title="KPI Category Scores"
        subtitle={
          data.saved_at
            ? `Saved ${new Date(data.saved_at).toLocaleString()} · ${data.category_rows} category + ${data.overall_rows} overall rows`
            : undefined
        }
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
          <ScoreTile
            label="Overall (composite)"
            score={data.composite}
            featured
          />
          {data.categories.map(c => (
            <ScoreTile key={c.category_key} label={c.label} score={c.score} />
          ))}
        </div>
      </TabCard>

      <div>
        <div className="flex flex-col gap-2.5 border-b border-gray-200 px-4 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 overflow-x-auto">
            <TabsComponent
              tabs={DETAIL_TABS}
              activeTab={detailTab}
              onTabChange={id => setDetailTab(id as DetailTab)}
              variant="tab-underline"
              className="mb-0 w-max max-w-none flex-nowrap whitespace-nowrap"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 pb-2 sm:pb-0">
            {detailTab === "per-kpi" ? (
              <>
                <DropdownSelector
                  label="All categories"
                  selected={category}
                  options={[
                    { value: "all", label: "All categories" },
                    ...data.categories.map(c => ({
                      value: c.category_key,
                      label: c.label,
                    })),
                  ]}
                  onSelect={setCategory}
                  enableScroll
                />
                <div className="w-[220px] shrink-0">
                  <SearchInput
                    value={term}
                    onChange={e => setTerm(e.target.value)}
                    placeholder="Search metric…"
                  />
                </div>
              </>
            ) : (
              <DropdownSelector
                label="Range"
                selected={trendRange}
                options={[
                  { value: "3", label: "Last 3 months" },
                  { value: "6", label: "Last 6 months" },
                  { value: "12", label: "Last 12 months" },
                ]}
                onSelect={v => setTrendRange(v as TrendRange)}
              />
            )}
          </div>
        </div>

        <div className="p-4">
          {detailTab === "per-kpi" ? (
            <Table
              className="[&_th]:normal-case [&_th]:tracking-normal"
              columns={kpiColumns}
              data={perKpi}
              rowKey={record => record.metric_id}
              scrollable
              maxHeight={420}
              emptyContent={
                <p className="text-sm text-gray-500">
                  No KPIs match these filters.
                </p>
              }
            />
          ) : (
            <TrendChart points={trendPoints} />
          )}
        </div>
      </div>
    </div>
  );
}

export default CategoryScoresTab;
